import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { fabricSewingTypeAPI } from "../../services/fabricSewingTypeAPI";
import { useNotification } from "../../hooks/useNotification";

const FabricSewingTypeList = () => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();
  const isRTL = i18n.language === "ar";
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterActive, setFilterActive] = useState("");

  useEffect(() => {
    loadTypes();
  }, [search, filterActive]);

  const loadTypes = async () => {
    try {
      setLoading(true);
      const params = { search };
      if (filterActive !== "") {
        params.is_active = filterActive === "true";
      }
      const response = await fabricSewingTypeAPI.getAll(params);
      // API returns { data: { data: [...], pagination: ... } }
      const typeList = response.data.data?.data || response.data.data || [];
      if (Array.isArray(typeList)) {
        setTypes(typeList);
      } else {
        setTypes([]);
      }
    } catch (error) {
      console.error("Error loading types:", error);
      showError(t("messages.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("messages.confirmDelete"))) return;
    try {
      await fabricSewingTypeAPI.delete(id);
      loadTypes();
    } catch (error) {
      console.error("Error deleting type:", error);
      showError(t("messages.error"));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">
            {t("fabricSewingTypes.title")}
          </h2>
          <p className="text-gray-600 mt-2">
            {t("fabricSewingTypes.subtitle")}
          </p>
        </div>
        <Link
          to="/fabric-sewing-types/new"
          className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
        >
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          {t("fabricSewingTypes.add")}
        </Link>
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {/* Filters */}
        <div className="p-6 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <svg
                className="absolute left-3 top-3.5 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder={t("common.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">
                {t("common.all")} {t("common.status")}
              </option>
              <option value="true">{t("fabricSewingTypes.active")}</option>
              <option value="false">{t("fabricSewingTypes.inactive")}</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className={`px-6 py-4 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-semibold text-gray-700 uppercase tracking-wider`}
                >
                  {t("fabricSewingTypes.name")}
                </th>
                <th
                  className={`px-6 py-4 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-semibold text-gray-700 uppercase tracking-wider`}
                >
                  {t("fabricSewingTypes.name_ar")}
                </th>
                <th
                  className={`px-6 py-4 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-semibold text-gray-700 uppercase tracking-wider`}
                >
                  {t("fabricSewingTypes.code")}
                </th>
                <th
                  className={`px-6 py-4 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-semibold text-gray-700 uppercase tracking-wider`}
                >
                  {t("fabricSewingTypes.description")}
                </th>
                <th
                  className={`px-6 py-4 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-semibold text-gray-700 uppercase tracking-wider`}
                >
                  {t("common.status")}
                </th>
                <th
                  className={`px-6 py-4 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-semibold text-gray-700 uppercase tracking-wider`}
                >
                  {t("common.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {types.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        className="h-12 w-12 text-gray-400 mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                        />
                      </svg>
                      {t("common.noData")}
                    </div>
                  </td>
                </tr>
              ) : (
                types.map((type) => (
                  <tr
                    key={type.id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td
                      className={`px-6 py-4 whitespace-nowrap ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      <span className="text-sm font-semibold text-gray-900">
                        {type.name}
                      </span>
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      <span
                        className="text-sm font-semibold text-gray-900"
                        dir="rtl"
                      >
                        {type.name_ar || "-"}
                      </span>
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      <span className="text-sm text-gray-600 bg-gray-100 px-2.5 py-1 rounded">
                        {type.code}
                      </span>
                    </td>
                    <td
                      className={`px-6 py-4 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      <span className="text-sm text-gray-600">
                        {type.description || "-"}
                      </span>
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      <span
                        className={`px-3 py-1.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          type.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {type.is_active
                          ? t("fabricSewingTypes.active")
                          : t("fabricSewingTypes.inactive")}
                      </span>
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      <div
                        className={`flex items-center ${
                          isRTL ? "space-x-reverse" : ""
                        } space-x-3`}
                      >
                        <Link
                          to={`/fabric-sewing-types/${type.id}/edit`}
                          className="text-green-600 hover:text-green-900 hover:bg-green-50 px-3 py-1 rounded transition-colors duration-200"
                          title={t("common.edit")}
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </Link>
                        <button
                          onClick={() => handleDelete(type.id)}
                          className="text-red-600 hover:text-red-900 hover:bg-red-50 px-3 py-1 rounded transition-colors duration-200"
                          title={t("common.delete")}
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FabricSewingTypeList;
